import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CrudForm, CrudStyles } from '../shared/Crud';
import { useForm } from '../../hooks/useForm';
import costCenterService from '../../services/costCenterService';
import CostCenterEmployeesList from './CostCenterEmployeesList';
import CostCenterAssociateEmployeesModal from './CostCenterAssociateEmployeesModal';

const { FormGroup, FormRow, Input, CheckboxGroup, Button, SectionTitle } = CrudStyles;

const sectionGap = {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    marginTop: 8,
};

const helperMuted = {
    margin: 0,
    fontSize: 13,
    color: '#64748b',
    lineHeight: 1.5,
    maxWidth: '36rem',
};

function validateCostCenterDetail(data) {
    const err = {};
    if (!data.name?.trim()) {
        err.name = 'Nome é obrigatório';
    }
    return err;
}

/**
 * Consulta e edição de centro de custo (dados iniciais vindos da listagem).
 * No envio, sempre envia name, code (ou null) e active.
 */
export default function CostCenterDetailForm({ customerId, costCenter, onCancel, onSuccess }) {
    const initialFormData = useMemo(
        () => ({
            name: costCenter?.name ?? '',
            code: costCenter?.code ?? '',
            active: Boolean(costCenter?.active),
        }),
        [costCenter],
    );

    const costCenterId = costCenter?.id;

    const [showEligibleEmployees, setShowEligibleEmployees] = useState(false);
    const [employeesRefreshTick, setEmployeesRefreshTick] = useState(0);

    useEffect(() => {
        setShowEligibleEmployees(false);
        setEmployeesRefreshTick(0);
    }, [costCenterId]);

    const submit = useCallback(
        async (data) => {
            if (costCenterId == null) {
                throw new Error('Identificador do centro de custo inválido.');
            }
            await costCenterService.update(customerId, costCenterId, {
                name: data.name.trim(),
                code: data.code?.trim() ? data.code.trim() : null,
                active: data.active,
            });
            onSuccess?.();
        },
        [customerId, costCenterId, onSuccess],
    );

    const { formData, errors, handleChange, handleSubmit, isSubmitting } = useForm(
        initialFormData,
        submit,
        validateCostCenterDetail,
    );

    const title =
        costCenterId != null ? `Centro de custo #${costCenterId}` : 'Centro de custo';

    return (
        <>
            <CrudForm title={title} onSubmit={handleSubmit} onCancel={onCancel} isSubmitting={isSubmitting}>
                <SectionTitle>Dados do centro de custo</SectionTitle>
                <FormRow>
                    <FormGroup>
                        <label htmlFor="cost-center-detail-name">Nome *</label>
                        <Input
                            id="cost-center-detail-name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            $hasError={!!errors.name}
                            autoComplete="off"
                        />
                        {errors.name && <span className="error">{errors.name}</span>}
                    </FormGroup>
                    <FormGroup>
                        <label htmlFor="cost-center-detail-code">Código</label>
                        <Input
                            id="cost-center-detail-code"
                            name="code"
                            value={formData.code}
                            onChange={handleChange}
                            $hasError={!!errors.code}
                            autoComplete="off"
                        />
                        {errors.code && <span className="error">{errors.code}</span>}
                    </FormGroup>
                </FormRow>
                <FormRow>
                    <FormGroup>
                        <span
                            style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: '#555',
                                textTransform: 'uppercase',
                                letterSpacing: '0.02em',
                                display: 'block',
                                marginBottom: 8,
                            }}
                        >
                            Situação
                        </span>
                        <CheckboxGroup>
                            <input
                                type="checkbox"
                                id="cost-center-detail-active"
                                name="active"
                                checked={!!formData.active}
                                onChange={handleChange}
                            />
                            <label htmlFor="cost-center-detail-active">Ativo</label>
                        </CheckboxGroup>
                    </FormGroup>
                </FormRow>
                {errors.submit && (
                    <span className="error" style={{ display: 'block', marginTop: 8 }}>
                        {errors.submit}
                    </span>
                )}
            </CrudForm>
            {costCenterId != null && (
                <div style={sectionGap}>
                    <SectionTitle>Colaboradores</SectionTitle>
                    <div
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 12,
                        }}
                    >
                        <p style={helperMuted}>
                            <strong>A associação de colaboradores cadastrados a um centro de custo serve para otimizar a cobrança de entregas e emissão de relatórios.</strong>
                        </p>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setShowEligibleEmployees((v) => !v)}
                            title={
                                showEligibleEmployees
                                    ? 'Fechar a janela de associação'
                                    : 'Abrir lista de colaboradores disponíveis para associar'
                            }
                        >
                            {showEligibleEmployees ? 'Fechar associação' : 'Associar colaborador'}
                        </Button>
                    </div>

                    {showEligibleEmployees && (
                        <CostCenterAssociateEmployeesModal
                            open={showEligibleEmployees}
                            customerId={customerId}
                            costCenterId={costCenterId}
                            idPrefix={`cc-detail-${costCenterId}-eligible`}
                            onClose={() => setShowEligibleEmployees(false)}
                            onAssociated={() => {
                                setShowEligibleEmployees(false);
                                setEmployeesRefreshTick((v) => v + 1);
                            }}
                        />
                    )}

                    <CostCenterEmployeesList
                        customerId={customerId}
                        costCenterId={costCenterId}
                        idPrefix={`cc-detail-${costCenterId}-emp`}
                        refreshTrigger={employeesRefreshTick}
                        hideListHeader
                    />
                </div>
            )}
        </>
    );
}
